import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ReactComponent as CloseIcon } from "../assets/closeIcon.svg";

import { generateUUID } from "../../../utils";
import { getFeatures, setFeatures } from "../../../store/slices/currentMap";
import { getProjects } from "../../../store/slices/projects";
import { getCurrentMapId } from "../../../store/slices/currentMap";
import { getModalData, toggleModal } from "../../../store/slices/modals";
import {
  createStation,
  updateStation,
  deleteStation,
  StationPayload,
} from "../../../services/stationApi";

import {
  ActionButtonStyled,
  HeaderStyled,
  InputStyled,
  ModalWrapStyled,
  StationModalContentStyled,
  StationModalStyled,
  LabelStyled,
  ConfirmationTextStyled,
  ButtonContainerStyled,
  CancelButtonStyled,
  DeleteButtonStyled,
} from "./styles";
import { FeaturesInterface } from "../../../models/map-interface";

const StationModal = () => {
  const dispatch = useDispatch();
  const mapFeatures = useSelector(getFeatures);
  const modalData = useSelector(getModalData);
  const [stationName, setStationName] = useState("");
  const [markerColor] = useState("#FF0000");
  const [markerSize] = useState("medium");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projects = useSelector(getProjects);
  const currentMapId = useSelector(getCurrentMapId);
  const currentProject = projects.find((p) => p._id === currentMapId);
  const [coordinates, setCoordinates] = useState<string>("");

  // Check if this is edit mode
  const isEditMode = modalData?.station !== undefined;

  // Check if this is delete confirmation mode
  const isDeleteMode = modalData?.showDeleteConfirmation === true;

  useEffect(() => {
    if (isEditMode && modalData?.station) {
      // Populate form with existing station data
      const station = modalData.station;
      setStationName(station.name || "");
      setCoordinates(`${station.latitude}, ${station.longitude}`);
    } else if (modalData?.coordinate && modalData.coordinate.length === 2) {
      // Create mode with coordinates
      setCoordinates(`${modalData.coordinate[1]}, ${modalData.coordinate[0]}`);
    }
  }, [modalData?.coordinate, modalData?.station, isEditMode]);

  const handleSubmit = async () => {
    if (!stationName) {
      toast.error("Please fill in the station name");
      return;
    }

    if (!currentProject?.semanticMapId) {
      toast.error("No semantic map associated with this project");
      return;
    }

    const coordsArr = coordinates.split(",").map((s) => parseFloat(s.trim()));
    if (coordsArr.length !== 2 || coordsArr.some(isNaN)) {
      toast.error("Invalid coordinates format");
      return;
    }
    const [latitude, longitude] = coordsArr;

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // Update existing station
        const updatePayload: Partial<StationPayload> = {
          name: stationName,
          short_name: stationName, // Use station name as short name
          latitude,
          longitude,
        };

        const response = await updateStation(
          modalData.station.id,
          updatePayload
        );

        if (!response?.success) {
          throw new Error("Failed to update station");
        }

        toast.success("Station updated successfully");

        // Dispatch custom event to notify SideMenu to refresh stations
        const stationsUpdatedEvent = new CustomEvent("stationsUpdated", {
          detail: {
            semanticMapId: currentProject.semanticMapId,
            action: "updated",
            stationId: modalData.station.id,
          },
        });
        window.dispatchEvent(stationsUpdatedEvent);
      } else {
        // Create new station
        const stationId = generateUUID();
        const featureId = generateUUID();

        // Create station in the API - short_name will be the same as name
        const stationPayload: StationPayload = {
          name: stationName,
          short_name: stationName, // Use station name as short name
          semantic_map: currentProject.semanticMapId,
          latitude,
          longitude,
          utm_x: 0, // These values should be calculated based on the coordinates
          utm_y: 0,
          utm_zone: "10", // This should be determined based on the coordinates
          initialization: "0",
          marker_color: markerColor,
          marker_size: markerSize,
        };

        const response = await createStation(stationPayload);

        if (!response?.success) {
          throw new Error("Failed to create station");
        }

        // Update local state
        const newFeature = {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [longitude, latitude],
          },
          properties: {
            feature_id: featureId,
            feature_info_list: [
              {
                polygon_type: "station",
                station_name: stationName,
                short_name: stationName, // Use station name as short name
                marker_color: markerColor,
                marker_size: markerSize,
                station_id: stationId,
              },
            ],
          },
        };

        dispatch(
          setFeatures({
            ...mapFeatures,
            features: [...mapFeatures.features, newFeature],
          } as FeaturesInterface)
        );

        // Dispatch custom event to notify SideMenu to refresh stations
        const stationsUpdatedEvent = new CustomEvent("stationsUpdated", {
          detail: {
            semanticMapId: currentProject.semanticMapId,
            action: "created",
            stationId: stationId,
          },
        });
        window.dispatchEvent(stationsUpdatedEvent);

        toast.success("Station created successfully");
      }

      dispatch(toggleModal({ type: null }));
    } catch (error) {
      console.error("Error handling station:", error);
      toast.error(
        isEditMode ? "Failed to update station" : "Failed to create station"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !modalData?.station || !currentProject?.semanticMapId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await deleteStation(modalData.station.id);

      if (!response?.success) {
        throw new Error("Failed to delete station");
      }

      toast.success("Station deleted successfully");

      // Dispatch custom event to notify SideMenu to refresh stations
      const stationsUpdatedEvent = new CustomEvent("stationsUpdated", {
        detail: {
          semanticMapId: currentProject.semanticMapId,
          action: "deleted",
          stationId: modalData.station.id,
        },
      });
      window.dispatchEvent(stationsUpdatedEvent);

      dispatch(toggleModal({ type: null }));
    } catch (error) {
      console.error("Error deleting station:", error);
      toast.error("Failed to delete station");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render delete confirmation modal
  if (isDeleteMode) {
    return (
      <ModalWrapStyled>
        <StationModalStyled>
          <HeaderStyled>
            <CloseIcon onClick={() => dispatch(toggleModal({ type: null }))} />
            Delete Station
          </HeaderStyled>
          <StationModalContentStyled>
            <ConfirmationTextStyled>
              Are you sure you want to delete the station &quot;
              {modalData?.station?.name}&quot;?
            </ConfirmationTextStyled>
            <ButtonContainerStyled>
              <CancelButtonStyled
                onClick={() => dispatch(toggleModal({ type: null }))}
                disabled={isSubmitting}
              >
                Cancel
              </CancelButtonStyled>
              <DeleteButtonStyled
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Deleting..." : "Delete"}
              </DeleteButtonStyled>
            </ButtonContainerStyled>
          </StationModalContentStyled>
        </StationModalStyled>
      </ModalWrapStyled>
    );
  }

  return (
    <ModalWrapStyled>
      <StationModalStyled>
        <HeaderStyled>
          <CloseIcon onClick={() => dispatch(toggleModal({ type: null }))} />
          {isEditMode ? "Edit Station" : "Create Station"}
        </HeaderStyled>
        <StationModalContentStyled>
          <LabelStyled>
            Station name (25 characters max.)
          </LabelStyled>
          <InputStyled
            type="text"
            placeholder="Station name (25 characters max.)"
            value={stationName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setStationName(e.target.value)
            }
          />

          <LabelStyled>
            Station Coordinates
          </LabelStyled>
          <InputStyled
            type="text"
            value={coordinates}
            onChange={(e) => setCoordinates(e.target.value)}
          />

          <ActionButtonStyled onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update Station"
              : "Create Station"}
          </ActionButtonStyled>
        </StationModalContentStyled>
      </StationModalStyled>
    </ModalWrapStyled>
  );
};

export default StationModal;
